import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, Link } from 'react-router-dom';
import './SearchScreenNav.css';
import { fetchLawUpdates, updateNewsFlash, dismissNewsFlash } from '../services/lawUpdates';
import lgbtLogo from '../assets/lgbtpridepath.png';
import NewsFlashAdmin from './NewsFlashAdmin';

// Logo Component with image - UNIQUE CLASS NAMES (ssn- prefix)
const LGBTNearMeLogo = () => {
  return (
    <div className="ssn-logo-container">
      <div className="ssn-logo-content-wrapper">
        <img 
          src={lgbtLogo}
          alt="LGBT Pride Path"
          className="ssn-logo-image"
        />
        <div className="ssn-text-wrapper">
          <span className="ssn-text-finding">finding home</span>
          <span className="ssn-text-program">a program of the lgbt national help center</span>
        </div>
      </div>
    </div>
  );
};

// Dropdown Portal Component (unchanged for desktop)
const DropdownPortal = ({ children, isOpen, targetRef, onMouseEnter, onMouseLeave }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 5,
        left: rect.left
      });
    }
  }, [isOpen, targetRef]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div 
      className="ssn-dropdown-portal"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 150,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="ssn-dropdown-menu open">
        {children}
      </div>
    </div>,
    document.body
  );
};

// Mobile Menu Backdrop Component
const MobileMenuBackdrop = ({ isOpen, onClick }) => {
  if (!isOpen) return null;
  return (
    <div 
      className={`ssn-backdrop ${isOpen ? 'open' : ''}`}
      onClick={onClick}
      aria-hidden="true"
    />
  );
};

const SearchScreenNav = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isMobileAboutOpen, setIsMobileAboutOpen] = useState(false);
  const [lawUpdates, setLawUpdates] = useState([]);
  const [showNewsFlash, setShowNewsFlash] = useState(false);
  const [dismissedUpdates, setDismissedUpdates] = useState([]);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const clickTimerRef = useRef(null);
  const dropdownRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);
  const location = useLocation();

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileAboutOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownPortal = document.querySelector('.ssn-dropdown-portal');
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target) &&
          (!dropdownPortal || !dropdownPortal.contains(event.target))) {
        setIsAboutDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch law updates and reset dismissed updates on load
  useEffect(() => {
    localStorage.removeItem('dismissedLawUpdates');
    setDismissedUpdates([]);

    const fetchUpdates = async () => {
      try {
        const updates = await fetchLawUpdates();
        setLawUpdates(updates);
        setShowNewsFlash(updates.length > 0);
      } catch (error) {
        console.error('Error fetching law updates:', error);
      }
    };
    fetchUpdates();
  }, []);

  const handleDismissNewsFlash = async (updateId) => {
    try {
      await dismissNewsFlash(updateId);
      setDismissedUpdates([...dismissedUpdates, updateId]);
      setLawUpdates(lawUpdates.filter((update) => update.id !== updateId));
      if (lawUpdates.length <= 1) setShowNewsFlash(false);
    } catch (error) {
      console.error('Error dismissing news flash:', error);
    }
  };

  // Handle triple click on news flash
  const handleNewsFlashClick = () => {
    setClickCount(prev => prev + 1);
    
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    if (clickCount === 2) {
      setShowAdminModal(true);
      setClickCount(0);
    } else {
      clickTimerRef.current = setTimeout(() => {
        setClickCount(0);
      }, 500);
    }
  };

  // Handle news flash update from admin
  const handleUpdateNewsFlash = async (newText, credentials) => {
    try {
      console.log('Updating news flash with:', newText);
      const result = await updateNewsFlash({ 
        id: Date.now(), 
        lawName: newText,
        state: '', 
      }, credentials);
      
      console.log('Update result:', result);
      const updates = await fetchLawUpdates();
      setLawUpdates(updates);
      setShowNewsFlash(updates.length > 0);
      setDismissedUpdates([]);
      setShowAdminModal(false);
    } catch (error) {
      console.error('Error updating news flash:', error);
      throw error;
    }
  };

  const getCurrentNewsText = () => {
    const activeUpdates = lawUpdates.filter(update => !dismissedUpdates.includes(update.id));
    if (activeUpdates.length > 0) {
      const update = activeUpdates[0];
      return update.lawName;
    }
    return '';
  };

  // Handle dropdown hover with delay
  const handleDropdownMouseEnter = () => {
    clearTimeout(dropdownTimeoutRef.current);
    setIsAboutDropdownOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsAboutDropdownOpen(false);
    }, 100);
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (!isMobileMenuOpen) {
      setIsMobileAboutOpen(false); // Close submenu when opening main menu
    }
  };

  // Handle mobile navigation link click
  const handleMobileLinkClick = () => {
    setIsMobileMenuOpen(false);
    setIsMobileAboutOpen(false);
  };

  return (
    <>
      <div className="ssn-wrapper">
        <nav className={`ssn-navbar ssn-lgbt-style ${isScrolled ? 'ssn-scrolled' : ''}`}>
          <div className="ssn-outer-container">
            <div className="ssn-top-section">
              <div className="ssn-logo">
                <Link to="/" className="ssn-logo-link">
                  <LGBTNearMeLogo />
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="ssn-links-desktop">
                <Link to="/" className="ssn-nav-link">HOME</Link>
                
                {/* About Dropdown */}
                <div 
                  className="ssn-dropdown-wrapper" 
                  ref={dropdownRef}
                  onMouseEnter={handleDropdownMouseEnter}
                  onMouseLeave={handleDropdownMouseLeave}
                >
                  <span className="ssn-nav-link ssn-dropdown-trigger">
                    ABOUT
                  </span>
                </div>

                {/* Portal for dropdown menu */}
                <DropdownPortal 
                  isOpen={isAboutDropdownOpen} 
                  targetRef={dropdownRef}
                  onMouseEnter={handleDropdownMouseEnter}
                  onMouseLeave={handleDropdownMouseLeave}
                >
                  <Link 
                    to="/about" 
                    className="ssn-dropdown-item"
                    onClick={() => setIsAboutDropdownOpen(false)}
                  >
                    About Us
                  </Link>
                  <Link 
                    to="/methodology" 
                    className="ssn-dropdown-item"
                    onClick={() => setIsAboutDropdownOpen(false)}
                  >
                    Methodology
                  </Link>
                  <Link 
                    to="/media-tool-kit" 
                    className="ssn-dropdown-item"
                    onClick={() => setIsAboutDropdownOpen(false)}
                  >
                    Media Tool Kit
                  </Link>
                  <Link 
                    to="/contact" 
                    className="ssn-dropdown-item"
                    onClick={() => setIsAboutDropdownOpen(false)}
                  >
                    Contact
                  </Link>
                </DropdownPortal>

                <Link to="/programs" className="ssn-nav-link">PROGRAMS</Link>
                <Link to="https://www.lgbtnearme.org/" className="ssn-nav-link" style={{ textTransform: 'none' }}>LGBTnearMe.org</Link>
                <Link to="https://givebutter.com/lgbthotline" className="ssn-nav-link">DONATE</Link>
              </div>

              {/* Mobile Menu Button */}
              <button 
                className="ssn-mobile-button" 
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                <div className={`ssn-menu-icon ${isMobileMenuOpen ? 'open' : ''}`}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </button>
            </div>
          </div>
        </nav>

        {/* News Flash Banner */}
        {showNewsFlash && lawUpdates.length > 0 && (
          <div 
            className="ssn-news-flash" 
            role="alert" 
            aria-live="polite"
            onClick={handleNewsFlashClick}
          >
            {lawUpdates
              .filter((update) => !dismissedUpdates.includes(update.id))
              .map((update) => (
                <div key={update.id} className="ssn-news-item">
                  <span className="ssn-news-text">
                    {update.lawName}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDismissNewsFlash(update.id);
                    }}
                    className="ssn-news-dismiss"
                    aria-label={`Dismiss ${update.lawName} update`}
                  >
                    ✕
                  </button>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Mobile Menu Backdrop */}
      <MobileMenuBackdrop 
        isOpen={isMobileMenuOpen} 
        onClick={toggleMobileMenu}
      />

      {/* Mobile Navigation Menu */}
      <div className={`ssn-mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <Link 
          to="/" 
          className="ssn-mobile-link"
          onClick={handleMobileLinkClick}
        >
          HOME
        </Link>
        
        {/* Mobile About with Submenu */}
        <div className="ssn-mobile-dropdown">
          <button 
            className="ssn-mobile-link ssn-mobile-trigger"
            onClick={() => setIsMobileAboutOpen(!isMobileAboutOpen)}
            aria-expanded={isMobileAboutOpen}
          >
            ABOUT
            <span className={`ssn-mobile-arrow ${isMobileAboutOpen ? 'open' : ''}`}>▼</span>
          </button>
          <div className={`ssn-submenu ${isMobileAboutOpen ? 'open' : ''}`}>
            <Link 
              to="/about" 
              className="ssn-submenu-link"
              onClick={handleMobileLinkClick}
            >
              About Us
            </Link>
            <Link 
              to="/methodology" 
              className="ssn-submenu-link"
              onClick={handleMobileLinkClick}
            >
              Methodology
            </Link>
            <Link 
              to="/media-tool-kit" 
              className="ssn-submenu-link"
              onClick={handleMobileLinkClick}
            >
              Media Tool Kit
            </Link>
            <Link 
              to="/contact" 
              className="ssn-submenu-link"
              onClick={handleMobileLinkClick}
            >
              Contact
            </Link>
          </div>
        </div>

        <Link 
          to="/programs" 
          className="ssn-mobile-link"
          onClick={handleMobileLinkClick}
        >
          PROGRAMS
        </Link>
        <Link 
          to="https://www.lgbtnearme.org/" 
          className="ssn-mobile-link"
          onClick={handleMobileLinkClick}
          style={{ textTransform: 'none' }}
        >
          LGBTnearMe.org
        </Link>
        <Link 
          to="https://givebutter.com/lgbthotline" 
          className="ssn-mobile-link"
          onClick={handleMobileLinkClick}
        >
          DONATE
        </Link>
      </div>

      {/* Admin Modal */}
      {showAdminModal && (
        <NewsFlashAdmin
          currentNewsText={getCurrentNewsText()}
          onUpdateNews={handleUpdateNewsFlash}
          onClose={() => setShowAdminModal(false)}
        />
      )}
    </>
  );
};

export default SearchScreenNav;